import React, { useEffect, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import remarkGfm from 'remark-gfm';
import T from 'i18n';
import styles from '../CaseIntelligenceVibrantPage.module.scss';
import {
  AcceptCaseTagSuggestionRequest,
  CaseArtefact,
  CaseTagSuggestion,
  RejectCaseTagSuggestionRequest,
} from '../types';
import {
  buildDashboardModel,
  DashboardQuestion,
  DashboardTabId,
  getTimelineRangeLabel,
} from '../utils/dashboardModel';
import ConfidenceRing from './ConfidenceRing';
import TagSuggestionsPanel from './TagSuggestionsPanel';
import { formatConfidenceScore, getConfidenceTone } from '../utils/confidence';

interface Props {
  artefacts: CaseArtefact[];
  tagSuggestions?: CaseTagSuggestion[] | null;
  tagSuggestionsPending?: boolean;
  tagSuggestionsFeedback?: { error?: unknown } | null;
  onRetryTags?: () => void;
  onAcceptTagSuggestion?: (payload: AcceptCaseTagSuggestionRequest) => Promise<unknown> | unknown;
  onRejectTagSuggestion?: (payload: RejectCaseTagSuggestionRequest) => Promise<unknown> | unknown;
}

type DashboardUiTabId = DashboardTabId | 'tags';

const TAGS_TAB = { id: 'tags' as const, label: 'Tags' };

const PRIORITY_LABELS: Record<DashboardQuestion['priority'], string> = {
  critical: 'caseIntelligence.priorityCritical',
  high: 'caseIntelligence.priorityHigh',
  medium: 'caseIntelligence.priorityMedium',
  low: 'caseIntelligence.priorityLow',
};

function formatRelativeTime(dateString: string | null) {
  if (!dateString) {
    return T.translate('caseIntelligence.unavailable');
  }

  const target = new Date(dateString);
  if (Number.isNaN(target.getTime())) {
    return dateString;
  }

  const deltaMs = Date.now() - target.getTime();
  const absDeltaMs = Math.abs(deltaMs);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (absDeltaMs < hour) {
    const minutes = Math.max(1, Math.round(absDeltaMs / minute));
    return T.translate('caseIntelligence.minutesAgo', { minutes });
  }

  if (absDeltaMs < day) {
    const hours = Math.max(1, Math.round(absDeltaMs / hour));
    return T.translate('caseIntelligence.hoursAgo', { hours });
  }

  const days = Math.max(1, Math.round(absDeltaMs / day));
  return T.translate('caseIntelligence.daysAgo', { days });
}

function formatAbsoluteDate(dateString: string | null) {
  if (!dateString) {
    return T.translate('caseIntelligence.unknownDate');
  }

  const target = new Date(dateString);
  if (Number.isNaN(target.getTime())) {
    return dateString;
  }

  return target.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatMetricCount(value: number, singular: string, plural: string) {
  return `${value} ${value === 1 ? singular : plural}`;
}

function humanizeToken(value: string | null | undefined) {
  if (!value) {
    return T.translate('caseIntelligence.unknown');
  }

  return value
    .split(/[_-]/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function getPriorityClass(priority: DashboardQuestion['priority']) {
  switch (priority) {
    case 'critical':
      return styles.priorityCritical;
    case 'high':
      return styles.priorityHigh;
    case 'medium':
      return styles.priorityMedium;
    default:
      return styles.priorityLow;
  }
}

function getConfidenceBadgeClass(score?: number | null, level?: string | null) {
  switch (getConfidenceTone(score, level)) {
    case 'positive':
      return styles.confidenceBadgePositive;
    case 'moderate':
      return styles.confidenceBadgeModerate;
    case 'low':
      return styles.confidenceBadgeLow;
    default:
      return '';
  }
}

function renderConfidenceScoreBadge(score?: number | null, level?: string | null) {
  const formattedScore = formatConfidenceScore(score, level);
  if (!formattedScore) {
    return null;
  }

  return (
    <span className={`${styles.softBadge} ${getConfidenceBadgeClass(score, level)}`}>
      {T.translate('caseIntelligence.confidenceScoreLabel')}: {formattedScore}
    </span>
  );
}

const CaseIntelligenceDashboard: React.FC<Props> = ({
  artefacts,
  tagSuggestions = null,
  tagSuggestionsPending = false,
  tagSuggestionsFeedback = null,
  onRetryTags = () => {},
  onAcceptTagSuggestion,
  onRejectTagSuggestion,
}) => {
  const dashboard = buildDashboardModel(artefacts);
  const availableTabs = useMemo(() => [...dashboard.tabs, TAGS_TAB], [dashboard.tabs]);
  const [activeTab, setActiveTab] = useState<DashboardUiTabId | null>(availableTabs[0]?.id || null);
  const [partySearch, setPartySearch] = useState('');
  const [glossarySearch, setGlossarySearch] = useState('');
  const [expandedIssues, setExpandedIssues] = useState<Record<string, boolean>>({});
  const [showFullOverview, setShowFullOverview] = useState(false);
  const [showSecondaryQuestions, setShowSecondaryQuestions] = useState(false);

  useEffect(() => {
    const activeTabStillExists = availableTabs.some((tab) => tab.id === activeTab);
    if (!activeTabStillExists) {
      setActiveTab(availableTabs[0]?.id || null);
    }
  }, [activeTab, availableTabs]);

  const filteredParties =
    dashboard.parties.data?.parties.filter((party) => {
      if (!partySearch.trim()) {
        return true;
      }

      const value = partySearch.toLowerCase();
      return (
        party.name.toLowerCase().includes(value) ||
        (party.description || '').toLowerCase().includes(value) ||
        (party.aliases || []).some((alias) => alias.toLowerCase().includes(value))
      );
    }) || [];

  const filteredTerms =
    dashboard.glossary.data?.terms.filter((term) => {
      if (!glossarySearch.trim()) {
        return true;
      }

      const value = glossarySearch.toLowerCase();
      return (
        term.term.toLowerCase().includes(value) ||
        term.definition.toLowerCase().includes(value) ||
        (term.aliases || []).some((alias) => alias.toLowerCase().includes(value))
      );
    }) || [];

  const groupedTerms = filteredTerms.reduce(
    (acc, term) => {
      const letter = term.term.charAt(0).toUpperCase();
      acc[letter] = acc[letter] || [];
      acc[letter].push(term);
      return acc;
    },
    {} as Record<string, typeof filteredTerms>,
  );

  const groupedTimelineEvents = (dashboard.timeline.data?.events || []).reduce(
    (acc, event) => {
      const year = event.date ? new Date(event.date).getFullYear().toString() : 'Unknown';
      acc[year] = acc[year] || [];
      acc[year].push(event);
      return acc;
    },
    {} as Record<string, NonNullable<typeof dashboard.timeline.data>['events']>,
  );

  const years = Object.keys(groupedTimelineEvents).sort();
  const glossaryLetters = Object.keys(groupedTerms).sort();
  const timelineRangeLabel = getTimelineRangeLabel(dashboard);
  const weaknessSupportingDocuments = Array.from(
    new Set(
      dashboard.overview.weaknesses.flatMap((weakness) => weakness.supportingDocuments || []),
    ),
  );

  const toggleIssue = (issueTitle: string) => {
    setExpandedIssues((current) => ({
      ...current,
      [issueTitle]: !current[issueTitle],
    }));
  };

  const renderQuestionCards = (questions: DashboardQuestion[]) => (
    <div className={styles.questionList}>
      {questions.map((question, index) => (
        <div key={`${question.question}-${index}`} className={styles.questionCard}>
          <div className={styles.questionMeta}>
            <span className={`${styles.priorityBadge} ${getPriorityClass(question.priority)}`}>
              {T.translate(PRIORITY_LABELS[question.priority])}
            </span>
            {question.category ? (
              <span className={styles.softBadge}>{humanizeToken(question.category)}</span>
            ) : null}
          </div>
          <div className={styles.questionText}>{question.question}</div>
          {question.suggestedAction ? (
            <div className={styles.questionAction}>{question.suggestedAction}</div>
          ) : null}
        </div>
      ))}
    </div>
  );

  const renderFileListBadge = (label: string, fileIds: string[]) => {
    return (
      <Tooltip
        title={
          <div className={styles.sourceTooltip}>
            <div className={styles.sourceTooltipTitle}>{label}</div>
            <ul className={styles.sourceTooltipList}>
              {fileIds.map((fileId) => (
                <li key={fileId}>{fileId}</li>
              ))}
            </ul>
          </div>
        }
        placement="top"
        arrow
      >
        <span
          className={`${styles.softBadge} ${styles.tooltipBadge} ${styles.fileReferenceBadge}`}
          tabIndex={0}
        >
          {formatMetricCount(fileIds.length, label.replace(/s$/, ''), label)}
        </span>
      </Tooltip>
    );
  };

  return (
    <div className={styles.dashboardShell}>
      <section className={styles.heroSection}>
        <div className={styles.heroGrid}>
          <ConfidenceRing
            value={dashboard.overview.confidence.value}
            percent={dashboard.overview.confidence.percent}
            score={dashboard.overview.confidence.score}
            label={T.translate('caseIntelligence.confidenceScoreLabel') as string}
          />

          <div className={styles.summaryCard}>
            <div className={styles.cardEyebrow}>{T.translate('caseIntelligence.summary')}</div>
            <p className={styles.summaryText}>
              {dashboard.overview.summary || T.translate('caseIntelligence.noSummaryAvailable')}
            </p>

            <div className={styles.metricChips}>
              <span className={styles.metricChip}>
                {T.translate('caseIntelligence.updated')}{' '}
                {formatRelativeTime(dashboard.overview.metrics.generatedAt)}
              </span>
              <span className={styles.metricChip}>
                {formatMetricCount(
                  dashboard.overview.metrics.documentCount,
                  T.translate('caseIntelligence.document') as string,
                  T.translate('caseIntelligence.documentsPlural') as string,
                )}
              </span>
              <span className={styles.metricChip}>
                {formatMetricCount(
                  dashboard.overview.metrics.openQuestionCount,
                  T.translate('caseIntelligence.openQuestion') as string,
                  T.translate('caseIntelligence.openQuestions') as string,
                )}
              </span>
              {timelineRangeLabel ? (
                <span className={styles.metricChip}>
                  {T.translate('caseIntelligence.timeline')} {timelineRangeLabel}
                </span>
              ) : null}
              <span className={styles.metricChip}>
                {formatMetricCount(
                  dashboard.overview.metrics.conflictCount,
                  T.translate('caseIntelligence.conflict') as string,
                  T.translate('caseIntelligence.conflicts') as string,
                )}
              </span>
            </div>
          </div>

          <div className={`${styles.highlightCard} ${styles.theoriesCard}`}>
            <div className={styles.highlightHeader}>
              {T.translate('caseIntelligence.coreTheory')}
            </div>
            <div className={styles.highlightList}>
              {dashboard.overview.theories.length ? (
                dashboard.overview.theories.map((theory) => (
                  <div key={`${theory.party}-${theory.title}`} className={styles.highlightItem}>
                    <div className={styles.highlightItemTitle}>{theory.title}</div>
                    <p className={styles.highlightItemBody}>{theory.summary}</p>
                    {theory.supportingAllegations.length ? (
                      <div className={styles.theorySection}>
                        <div className={styles.theorySectionTitle}>
                          {T.translate('caseIntelligence.supportingAllegations')}
                        </div>
                        <ul className={styles.theoryList}>
                          {theory.supportingAllegations.map((allegation) => (
                            <li key={allegation}>{allegation}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    <div className={styles.highlightMetaRow}>
                      <span className={styles.highlightTag}>{theory.party}</span>
                      {theory.supportingDocuments.length
                        ? renderFileListBadge(
                            T.translate('caseIntelligence.supportedDocuments') as string,
                            theory.supportingDocuments,
                          )
                        : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyPanelCopy}>
                  {T.translate('caseIntelligence.noCaseTheories')}
                </div>
              )}
            </div>
          </div>

          <div
            className={`${styles.highlightCard} ${styles.highlightCardAlert} ${styles.gapsCard}`}
          >
            <div className={styles.highlightHeader}>
              {T.translate('caseIntelligence.weaknesses')}
            </div>
            <div className={styles.highlightList}>
              {dashboard.overview.weaknesses.length ? (
                dashboard.overview.weaknesses.map((weakness) => (
                  <div key={weakness.text} className={styles.highlightAlertItem}>
                    <span className={styles.highlightBullet} />
                    <div className={styles.highlightAlertContent}>
                      <p className={styles.highlightItemBody}>{weakness.text}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyPanelCopy}>
                  {T.translate('caseIntelligence.noWeaknessesHighlighted')}
                </div>
              )}
            </div>
            {weaknessSupportingDocuments.length ? (
              <div className={styles.highlightFooter}>
                {renderFileListBadge(
                  T.translate('caseIntelligence.supportedDocuments') as string,
                  weaknessSupportingDocuments,
                )}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {dashboard.overview.artefact?.content ? (
        <section className={styles.fullOverviewSection}>
          <button
            type="button"
            className={styles.fullOverviewToggle}
            onClick={() => setShowFullOverview((current) => !current)}
            aria-expanded={showFullOverview}
          >
            <div className={styles.fullOverviewHeading}>
              <div className={styles.cardEyebrow}>
                {T.translate('caseIntelligence.fullCaseOverview')}
              </div>
            </div>
            {showFullOverview ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </button>

          {showFullOverview ? (
            <div className={styles.fullOverviewBody}>
              <div className={styles.markdownContent}>
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  skipHtml
                  components={{
                    a: ({ children, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                  }}
                >
                  {dashboard.overview.artefact.content}
                </Markdown>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {availableTabs.length ? (
        <section className={styles.tabsSection}>
          <div className={styles.tabList} role="tablist" aria-label="Case intelligence sections">
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabButtonActive : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={styles.tabPanel}>
            {activeTab === 'action-items' ? (
              <div className={styles.actionItemsGrid}>
                <div className={styles.guidanceCard}>
                  <div className={styles.guidanceIconWrap}>
                    <TipsAndUpdatesOutlinedIcon />
                  </div>
                  <div className={styles.guidanceContent}>
                    <div className={styles.guidanceTitle}>
                      {T.translate('caseIntelligence.keyGuidance')}
                    </div>
                    {dashboard.questions.guidanceGroups.length ? (
                      <div className={styles.guidanceGroups}>
                        {dashboard.questions.guidanceGroups.map((group) => (
                          <div key={group.priority} className={styles.guidanceGroup}>
                            <div className={styles.guidanceGroupHeader}>
                              <span
                                className={`${styles.priorityBadge} ${getPriorityClass(group.priority)}`}
                              >
                                {T.translate(PRIORITY_LABELS[group.priority])}
                              </span>
                            </div>
                            <ul className={styles.guidanceList}>
                              {group.items.map((action, index) => (
                                <li key={`${group.priority}-${action}-${index}`}>{action}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.emptyPanelCopy}>
                        {T.translate('caseIntelligence.noSuggestedActions')}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.questionsCard}>
                  <div className={styles.sectionHeading}>
                    {T.translate('caseIntelligence.nextCallFocus')}
                  </div>
                  {dashboard.questions.visibleQuestions.length ? (
                    <>
                      {renderQuestionCards(dashboard.questions.visibleQuestions)}

                      {dashboard.questions.secondaryQuestions.length ? (
                        <div className={styles.secondaryQuestionsSection}>
                          <button
                            type="button"
                            className={styles.secondaryQuestionsToggle}
                            onClick={() => setShowSecondaryQuestions((current) => !current)}
                            aria-expanded={showSecondaryQuestions}
                          >
                            <span className={styles.secondaryQuestionsLabel}>
                              {T.translate('caseIntelligence.mediumLowPriority')}
                            </span>
                            {showSecondaryQuestions ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </button>

                          {showSecondaryQuestions ? (
                            <div className={styles.secondaryQuestionsBody}>
                              {renderQuestionCards(dashboard.questions.secondaryQuestions)}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className={styles.emptyPanelCopy}>
                      {T.translate('caseIntelligence.noActionItems')}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {activeTab === 'issues' ? (
              <div className={styles.issueList}>
                {dashboard.issues.data?.issues?.length ? (
                  dashboard.issues.data.issues.map((issue) => {
                    const isExpanded = expandedIssues[issue.title] ?? false;

                    return (
                      <div
                        key={issue.title}
                        className={`${styles.issueCard} ${
                          isExpanded ? styles.issueCardExpanded : ''
                        }`}
                      >
                        <button
                          type="button"
                          className={styles.issueCardHeader}
                          onClick={() => toggleIssue(issue.title)}
                          aria-expanded={isExpanded}
                        >
                          <div>
                            <div className={styles.issueTitle}>{issue.title}</div>
                            <div className={styles.issueMeta}>
                              <span className={styles.softBadge}>
                                {humanizeToken(issue.issueType)}
                              </span>
                              {renderConfidenceScoreBadge(issue.confidenceScore, issue.confidence)}
                              {issue.sourceFiles?.length
                                ? renderFileListBadge(
                                    T.translate('caseIntelligence.sourceFiles') as string,
                                    issue.sourceFiles,
                                  )
                                : null}
                            </div>
                          </div>
                          {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </button>

                        {isExpanded ? (
                          <div className={styles.issueBody}>
                            {issue.description ? (
                              <p className={styles.issueDescription}>{issue.description}</p>
                            ) : null}

                            {issue.elements?.length ? (
                              <div className={styles.issueElements}>
                                {issue.elements.map((element) => (
                                  <div key={element.description} className={styles.issueElement}>
                                    <div className={styles.issueElementText}>
                                      {element.description}
                                    </div>
                                    {element.status ? (
                                      <span className={styles.elementStatus}>
                                        {humanizeToken(element.status)}
                                      </span>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            ) : null}

                            {issue.claimantPosition || issue.defendantPosition ? (
                              <div className={styles.positionsGrid}>
                                {issue.claimantPosition ? (
                                  <div className={styles.positionCard}>
                                    <div className={styles.positionTitle}>
                                      {T.translate('caseIntelligence.claimantPosition')}
                                    </div>
                                    <p>{issue.claimantPosition}</p>
                                  </div>
                                ) : null}
                                {issue.defendantPosition ? (
                                  <div className={styles.positionCard}>
                                    <div className={styles.positionTitle}>
                                      {T.translate('caseIntelligence.defendantPosition')}
                                    </div>
                                    <p>{issue.defendantPosition}</p>
                                  </div>
                                ) : null}
                              </div>
                            ) : null}

                            {issue.relatedEntities?.length ? (
                              <div className={styles.entityChips}>
                                {issue.relatedEntities.map((entity) => (
                                  <span key={entity} className={styles.entityChip}>
                                    {entity}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyPanelCopy}>
                    {T.translate('caseIntelligence.noKeyIssuesAvailable')}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === 'timeline' ? (
              <div className={styles.timelinePanel}>
                {years.length ? (
                  years.map((year) => (
                    <div key={year} className={styles.timelineYearGroup}>
                      <div className={styles.timelineYear}>{year}</div>
                      <div className={styles.timelineEvents}>
                        {groupedTimelineEvents[year].map((event) => {
                          return (
                            <div
                              key={`${event.date}-${event.description}`}
                              className={styles.timelineEvent}
                            >
                              <div className={styles.timelineMarker} />
                              <div className={styles.timelineContent}>
                                <div className={styles.timelineEventHeader}>
                                  <div className={styles.timelineDate}>
                                    {formatAbsoluteDate(event.date)}
                                  </div>
                                  <div className={styles.timelineBadges}>
                                    {event.significance ? (
                                      <span className={styles.softBadge}>
                                        {humanizeToken(event.significance)}
                                      </span>
                                    ) : null}
                                    {renderConfidenceScoreBadge(
                                      event.confidenceScore,
                                      event.confidence,
                                    )}
                                    <Tooltip
                                      title={
                                        <div className={styles.sourceTooltip}>
                                          <div className={styles.sourceTooltipTitle}>
                                            {T.translate('caseIntelligence.sourceFiles')}
                                          </div>
                                          <ul className={styles.sourceTooltipList}>
                                            {event.sourceFiles.map((sourceFile) => (
                                              <li key={sourceFile}>{sourceFile}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      }
                                      placement="top"
                                      arrow
                                    >
                                      <span
                                        className={`${styles.softBadge} ${styles.timelineSourceBadge} ${styles.tooltipBadge} ${styles.fileReferenceBadge}`}
                                        tabIndex={0}
                                      >
                                        {formatMetricCount(
                                          event.sourceFiles.length,
                                          T.translate('caseIntelligence.sourceFile') as string,
                                          T.translate(
                                            'caseIntelligence.sourceFilesPlural',
                                          ) as string,
                                        )}
                                      </span>
                                    </Tooltip>
                                  </div>
                                </div>
                                <div className={styles.timelineDescription}>
                                  {event.description}
                                </div>
                                {event.actors?.length ? (
                                  <div className={styles.timelineActors}>
                                    {T.translate('caseIntelligence.actors')}{' '}
                                    {event.actors.join(', ')}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyPanelCopy}>
                    {T.translate('caseIntelligence.noTimelineEventsAvailable')}
                  </div>
                )}
              </div>
            ) : null}

            {activeTab === 'parties' ? (
              <div className={styles.listingPanel}>
                <div className={styles.searchRow}>
                  <TextField
                    value={partySearch}
                    onChange={(event) => setPartySearch(event.target.value)}
                    placeholder={T.translate('caseIntelligence.searchPartiesPlaceholder') as string}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                <div className={styles.partyGrid}>
                  {filteredParties.map((party) => (
                    <div key={party.name} className={styles.partyCard}>
                      <div className={styles.partyCardHeader}>
                        <div className={styles.partyName}>{party.name}</div>
                        <div className={styles.partyChips}>
                          {party.role ? (
                            <span className={styles.softBadge}>{party.role}</span>
                          ) : null}
                          {party.type ? (
                            <span className={styles.softBadge}>{party.type}</span>
                          ) : null}
                        </div>
                      </div>
                      {party.description ? (
                        <div className={styles.partyDescription}>{party.description}</div>
                      ) : null}
                      {party.aliases?.length ? (
                        <div className={styles.partyMeta}>
                          {T.translate('caseIntelligence.aliases')} {party.aliases.join(', ')}
                        </div>
                      ) : null}
                      {party.mentionCount != null ? (
                        <div className={styles.partyMeta}>
                          {T.translate('caseIntelligence.mentionedInDocuments', {
                            count: party.mentionCount,
                          })}
                        </div>
                      ) : null}
                      {party.sourceFiles?.length ? (
                        <div className={styles.partyMeta}>
                          {renderFileListBadge(
                            T.translate('caseIntelligence.sourceFiles') as string,
                            party.sourceFiles,
                          )}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>

                {dashboard.parties.data?.relationships?.length ? (
                  <div className={styles.relationshipsSection}>
                    <div className={styles.sectionHeading}>
                      {T.translate('caseIntelligence.relationships')}
                    </div>
                    <div className={styles.tableWrap}>
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th>{T.translate('caseIntelligence.tableFrom')}</th>
                            <th>{T.translate('caseIntelligence.tableRelationship')}</th>
                            <th>{T.translate('caseIntelligence.tableTo')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard.parties.data.relationships.map((relationship) => (
                            <tr
                              key={`${relationship.from}-${relationship.relationship}-${relationship.to}`}
                            >
                              <td>{relationship.from}</td>
                              <td>{relationship.relationship}</td>
                              <td>{relationship.to}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {activeTab === 'glossary' ? (
              <div className={styles.listingPanel}>
                <div className={styles.searchRow}>
                  <TextField
                    value={glossarySearch}
                    onChange={(event) => setGlossarySearch(event.target.value)}
                    placeholder={T.translate('caseIntelligence.searchTermsPlaceholder') as string}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                <div className={styles.glossaryGroups}>
                  {glossaryLetters.length ? (
                    glossaryLetters.map((letter) => (
                      <div key={letter} className={styles.glossaryGroup}>
                        <div className={styles.glossaryLetter}>{letter}</div>
                        <div className={styles.glossaryTermList}>
                          {groupedTerms[letter].map((term) => (
                            <div key={term.term} className={styles.glossaryCard}>
                              <div className={styles.glossaryTitleRow}>
                                <div className={styles.glossaryTerm}>{term.term}</div>
                                {term.category ? (
                                  <span className={styles.softBadge}>
                                    {humanizeToken(term.category)}
                                  </span>
                                ) : null}
                              </div>
                              <div className={styles.glossaryDefinition}>{term.definition}</div>
                              {term.aliases?.length ? (
                                <div className={styles.partyMeta}>
                                  {T.translate('caseIntelligence.aliases')}{' '}
                                  {term.aliases.join(', ')}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.emptyPanelCopy}>
                      {T.translate('caseIntelligence.noMatchingTerms')}
                    </div>
                  )}
                </div>

                {dashboard.glossary.data?.abbreviations?.length ? (
                  <div className={styles.relationshipsSection}>
                    <div className={styles.sectionHeading}>
                      {T.translate('caseIntelligence.abbreviations')}
                    </div>
                    <div className={styles.tableWrap}>
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th>{T.translate('caseIntelligence.tableAbbreviation')}</th>
                            <th>{T.translate('caseIntelligence.tableExpansion')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboard.glossary.data.abbreviations.map((abbreviation) => (
                            <tr key={abbreviation.abbreviation}>
                              <td>{abbreviation.abbreviation}</td>
                              <td>{abbreviation.expansion}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {activeTab === 'tags' ? (
              <TagSuggestionsPanel
                tagSuggestions={tagSuggestions}
                tagSuggestionsPending={tagSuggestionsPending}
                tagSuggestionsFeedback={tagSuggestionsFeedback}
                onRetry={onRetryTags}
                onAcceptSuggestion={onAcceptTagSuggestion}
                onRejectSuggestion={onRejectTagSuggestion}
              />
            ) : null}
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default CaseIntelligenceDashboard;
